import { RequestType } from '../enums/request-type.enum';
import type { DraftCounts } from '../shared/models/draft';

/** Describes one request type's Drafts tab and where its create form lives. */
export type DraftTab = {
    readonly requestType: RequestType;
    readonly label: string;
    /** The child route path under `draft-requests`. */
    readonly path: string;
    /** The existing create form for this request type. A draft is continued at `<createPath>/draft/<id>`. */
    readonly createPath: string;
    /** Which entry of {@link DraftCounts} counts this type's drafts. */
    readonly countKey: keyof DraftCounts;
};

/** The four Drafts tabs, in display order. This is the single place that maps request types to routes. */
export const DRAFT_TABS: readonly DraftTab[] = [
    { requestType: RequestType.Payment, label: 'Payment', path: 'payment-drafts', createPath: '/payment-request', countKey: 'payment' },
    { requestType: RequestType.ExpectFunds, label: 'Expected Funds', path: 'expect-fund-drafts', createPath: '/expect-fund', countKey: 'expectFunds' },
    { requestType: RequestType.TransferFund, label: 'Transfer Funds', path: 'transfer-drafts', createPath: '/transfer-funds-request', countKey: 'transferFund' },
    { requestType: RequestType.Journal, label: 'Journal', path: 'journal-drafts', createPath: '/journal-request', countKey: 'journal' }
];

/**
 * Finds the tab for a request type.
 * @param requestType The request type.
 */
export const draftTabFor = (requestType: RequestType): DraftTab => {
    const tab = DRAFT_TABS.find(candidate => candidate.requestType === requestType);
    if (!tab) {
        throw new Error(`No Drafts tab is defined for request type '${requestType}'.`);
    }
    return tab;
};

/**
 * How a draft is named to the user, for example "Payment draft 2". Drafts are numbered within their request
 * type, so the type is always part of the name: "draft 2" on its own could be any of the four.
 * @param requestType The draft's request type.
 * @param draftNumber The draft's number within that type.
 */
export const draftName = (requestType: RequestType, draftNumber: number): string =>
    `${draftTabFor(requestType).label} draft ${draftNumber}`;

/**
 * Narrows route data to a request type, so a mistyped route fails loudly instead of listing the wrong drafts.
 * @param value The `requestType` entry of the route's data.
 */
export const isRequestType = (value: unknown): value is RequestType =>
    DRAFT_TABS.some(tab => tab.requestType === value);

/**
 * The router commands that open a draft in its create form.
 * @param requestType The draft's request type.
 * @param draftId The Draft ID.
 */
export const continueDraftCommands = (requestType: RequestType, draftId: number): (string | number)[] =>
    [draftTabFor(requestType).createPath, 'draft', draftId];
